// Contract Terms Tab
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Save, FileText, Upload, X, Eye } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import RichTextEditor from '../ui/RichTextEditor';

const ContractTermsTab = ({ tenantId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [termsFile, setTermsFile] = useState(null); // { id, filename }
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    standard_terms_conditions: '',
    standard_withdrawal_fee_shipin: '',
    standard_withdrawal_fee_sale: ''
  });

  useEffect(() => {
    fetchSettings();
  }, [tenantId]);

  const fetchSettings = async () => {
    try {
      const response = await api.get(`/tenants/${tenantId}`);
      const settings = response.data.settings || {};
      setForm({
        standard_terms_conditions: settings.standard_terms_conditions || '',
        standard_withdrawal_fee_shipin: settings.standard_withdrawal_fee_shipin || '',
        standard_withdrawal_fee_sale: settings.standard_withdrawal_fee_sale || ''
      });
      if (settings.standard_terms_conditions_file_id) {
        // Fetch file info
        try {
          const fRes = await api.get(`/files/${settings.standard_terms_conditions_file_id}/info`).catch(() => null);
          if (fRes?.data) {
            setTermsFile({ id: settings.standard_terms_conditions_file_id, filename: fRes.data.original_filename || 'terms-document' });
          } else {
            setTermsFile({ id: settings.standard_terms_conditions_file_id, filename: 'Terms & Conditions Document' });
          }
        } catch {
          setTermsFile({ id: settings.standard_terms_conditions_file_id, filename: 'Terms & Conditions Document' });
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load contract settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.get(`/tenants/${tenantId}`);
      const currentSettings = response.data.settings || {};
      
      await api.put(`/tenants/${tenantId}`, {
        settings: {
          ...currentSettings,
          ...form,
          standard_terms_conditions_file_id: termsFile?.id || null,
        }
      });
      toast.success('Contract terms updated');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save contract terms');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['application/pdf'];
    if (!allowed.includes(file.type)) {
      toast.error('Please upload a PDF document');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', 'Standard Terms & Conditions');
      formData.append('category', 'terms_conditions');
      const res = await api.post('/files/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const fileId = res.data?.id;
      if (fileId) {
        setTermsFile({ id: fileId, filename: file.name });
        toast.success('Document uploaded');
      }
    } catch {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleViewFile = () => {
    if (!termsFile?.id) return;
    const token = localStorage.getItem('token');
    fetch(`${window.location.origin}/api/files/download/${termsFile.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => window.open(URL.createObjectURL(blob), '_blank'));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Standard Contract Terms
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Terms & Conditions</Label>
            <RichTextEditor
              value={form.standard_terms_conditions}
              onChange={(v) => setForm({...form, standard_terms_conditions: v})}
              placeholder="Enter your standard consignment contract terms and conditions..."
            />
            <p className="text-xs text-muted-foreground">
              These terms will be included as Exhibit A in all consignment contracts
            </p>
          </div>

          <div className="space-y-2 pt-2 border-t">
            <Label>Or Upload a PDF</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Upload a PDF document to use as the Exhibit A instead of the text above. If both are provided, the uploaded document takes priority.
            </p>
            {termsFile ? (
              <div className="flex items-center gap-3 p-3 border rounded bg-muted/20">
                <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="text-sm flex-1 truncate">{termsFile.filename}</span>
                <Button variant="ghost" size="sm" className="h-7 px-2" onClick={handleViewFile} title="View"><Eye className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="sm" className="h-7 px-2 text-red-500" onClick={() => setTermsFile(null)} title="Remove"><X className="w-3.5 h-3.5" /></Button>
              </div>
            ) : (
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Upload PDF
              </Button>
            )}
            <input ref={fileInputRef} type="file" accept=".pdf" className="hidden" onChange={handleFileUpload} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Standard Withdrawal Fees</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Ship-In Withdrawal Fee</Label>
            <Input
              value={form.standard_withdrawal_fee_shipin}
              onChange={(e) => setForm({...form, standard_withdrawal_fee_shipin: e.target.value})}
              placeholder="e.g., $500 or 5%"
            />
            <p className="text-xs text-muted-foreground">
              Fee charged when a horse is withdrawn after being shipped in
            </p>
          </div>

          <div className="space-y-2">
            <Label>Sale Withdrawal Fee</Label>
            <Input
              value={form.standard_withdrawal_fee_sale}
              onChange={(e) => setForm({...form, standard_withdrawal_fee_sale: e.target.value})}
              placeholder="e.g., $1000 or 10%"
            />
            <p className="text-xs text-muted-foreground">
              Fee charged when a horse is withdrawn from a sale
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
        </Button>
      </div>
    </div>
  );
};

export default ContractTermsTab;
